import React, { useState } from 'react';
import { getAvailableWalletProviders, switchWalletProvider, WALLET_TYPES } from '../EthersSetup.js';
import PrivateKeyWallet from '../wallet/PrivateKeyWallet.js';

/**
 * WalletSelection Component
 * 
 * Provides users with options to connect using different wallet providers:
 * - External Wallet (MetaMask, etc. if available)
 * - Private Key input
 * - Future: WalletConnect, Coinbase, etc.
 */
const WalletSelection = ({ onWalletConnected, onError }) => {
  const [selectedWalletType, setSelectedWalletType] = useState(null);
  const [showPrivateKeyWallet, setShowPrivateKeyWallet] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [availableProviders] = useState(() => getAvailableWalletProviders());

  const handleMetaMaskConnect = async () => {
    setIsConnecting(true);
    try {
      await switchWalletProvider(WALLET_TYPES.METAMASK);
      onWalletConnected();
    } catch (error) {
      onError(error.message);
    } finally {
      setIsConnecting(false);
    }
  };

  const handlePrivateKeyConnect = () => {
    setShowPrivateKeyWallet(true);
  };

  const handlePrivateKeySuccess = () => {
    setShowPrivateKeyWallet(false);
    onWalletConnected();
  };

  const handlePrivateKeyError = (error) => {
    onError(error);
  };

  const handlePrivateKeyCancel = () => {
    setShowPrivateKeyWallet(false);
  };

  // Check if MetaMask is available
  const metaMaskProvider = availableProviders.find(p => p.type === WALLET_TYPES.METAMASK);
  const isMetaMaskAvailable = metaMaskProvider && metaMaskProvider.isAvailable;

  if (showPrivateKeyWallet) {
    return (
      <PrivateKeyWallet
        onSuccess={handlePrivateKeySuccess}
        onError={handlePrivateKeyError}
        onCancel={handlePrivateKeyCancel}
      />
    );
  }

  return (
    <div className="container mx-auto px-4">
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">Connect Your Wallet</h1>
            <p className="text-gray-600">
              Choose how you'd like to connect to GameMaker. Each option has different benefits for gaming.
            </p>
          </div>

          <div className="space-y-4">
            {/* External Wallet Option */}
            <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                    <svg className="w-8 h-8 text-orange-600" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">External Wallet</h3>
                    <p className="text-sm text-gray-600">Browser extension wallet (MetaMask, Coinbase, etc.)</p>
                    {!isMetaMaskAvailable && (
                      <p className="text-sm text-red-600">Not installed</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleMetaMaskConnect}
                  disabled={!isMetaMaskAvailable || isConnecting}
                  className={`px-6 py-2 rounded-lg font-medium ${
                    isMetaMaskAvailable && !isConnecting
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isConnecting ? 'Connecting...' : isMetaMaskAvailable ? 'Connect' : 'Install'}
                </button>
              </div>
              <div className="mt-3 text-sm text-gray-500">
                <div className="flex items-center space-x-4">
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                    Familiar interface
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full mr-1"></span>
                    May have connectivity issues
                  </span>
                </div>
              </div>
              {!isMetaMaskAvailable && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-sm text-gray-600 mb-2">Install a compatible wallet:</p>
                  <div className="flex space-x-2">
                    <a
                      href="https://metamask.io/download/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm underline"
                    >
                      MetaMask
                    </a>
                    <span className="text-gray-400">|</span>
                    <a
                      href="https://www.coinbase.com/wallet"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm underline"
                    >
                      Coinbase Wallet
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Private Key Option */}
            <div className="border border-gray-200 rounded-lg p-4 hover:border-green-300 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-3a1 1 0 011-1h2.586l6.414-6.414a6 6 0 017.743-5.743L15 7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Custom RPC Wallet</h3>
                    <p className="text-sm text-gray-600">Use your own RPC endpoint</p>
                  </div>
                </div>
                <button
                  onClick={handlePrivateKeyConnect}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
                >
                  Connect
                </button>
              </div>
              <div className="mt-3 text-sm text-gray-500">
                <div className="flex items-center space-x-4 mb-2">
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                    Reliable events
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                    Faster transactions
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mr-1"></span>
                    Advanced users
                  </span>
                </div>
                <div className="flex items-center space-x-4 text-xs">
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-1"></span>
                    Security responsibility
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-1"></span>
                    No seed phrase backup
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mr-1"></span>
                    Local storage risks
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Benefits and Risks Comparison */}
          <div className="mt-8 space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-3">Why Choose Custom RPC Wallet?</h4>
              <div className="text-sm text-gray-600 space-y-2">
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Better event detection for real-time games like Risk</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>No dependency on browser extension connectivity</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Works on any device without installing extensions</span>
                </div>
              </div>
            </div>

            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <h4 className="font-semibold text-orange-800 mb-3">⚠️ Important Security Considerations</h4>
              <div className="text-sm text-orange-700 space-y-2">
                <div className="flex items-start">
                  <svg className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span><strong>Full responsibility:</strong> You're solely responsible for private key security and backup</span>
                </div>
                <div className="flex items-start">
                  <svg className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span><strong>No recovery:</strong> Lost private key = permanent loss of funds (no password reset)</span>
                </div>
                <div className="flex items-start">
                  <svg className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span><strong>Technical complexity:</strong> Requires understanding of wallet security best practices</span>
                </div>
                <div className="flex items-start">
                  <svg className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span><strong>Device security:</strong> Key stored locally - compromised device = compromised wallet</span>
                </div>
                <div className="flex items-start">
                  <svg className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span><strong>Plain text entry:</strong> Private key entered as plain text in browser input field</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>
              Choose your preferred connection method above to get started.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletSelection;